<script lang="ts">
    import { COUNTRIES } from "@/constants/countryInfo";
    import { DATES } from "@/constants/dates";
    import { ATTRIBUTE_TYPE } from "@apps/reviews/constants";
    import { AttributesFiltersEntity } from "@apps/reviews/entities/AttributesFiltersEntity";
    import FormManager from "@lib/FormManager/FormManager";
    import FormManagerController from "@lib/FormManager/FormManagerController.svelte";
    import { FormManagerErrors } from "@lib/FormManager/types";

    import BaseButton from "@components/Buttons/BaseButton.svelte";
    import FormLayout from "@components/FormLayout/FormLayout.svelte";
    import FormLayoutItem from "@components/FormLayout/FormLayoutItem.svelte";
    import FormMultipleChooseField from "@components/forms/FormChooseField/FormMultipleChooseField.svelte";
    import FormSingleChooseField from "@components/forms/FormSingleChooseField/FormSingleChooseField.svelte";
    import Range from "@components/forms/Range.svelte";
    import Dropdown from "@components/forms/Select/Dropdown.svelte";
    import SelectDate from "@components/forms/Select/SelectDate.svelte";
    import Icon from "@components/Icon.svelte";
    import CloseIcon from "@components/icons/CloseIcon.svelte";
    import T from "@components/T.svelte";

    export let filters:
        | AttributesFiltersEntity["customerFilters"]
        | AttributesFiltersEntity["productFilters"];
    export let errors: FormManagerErrors;
    export let namespace: string;
    export let filterType: "customerFilters" | "productFilters";
    export let selectedFilters: {
        [key: string]: number | string | number[] | string[];
    };
    export let filterFormManager: FormManager;
    let attributeFiltersRef: HTMLDivElement;

    const getNewFilterValueByType = (type: string) => {
        switch (type) {
            case ATTRIBUTE_TYPE.SINGLE_VALUE:
            case ATTRIBUTE_TYPE.LOCATION:
                return "";
            case ATTRIBUTE_TYPE.DATE:
                return { day: null, month: null, year: null };
            case ATTRIBUTE_TYPE.MULTI_VALUE:
                return [];
            case ATTRIBUTE_TYPE.RANGE:
            case ATTRIBUTE_TYPE.CENTERED_RANGE:
                return null;
            default:
                return "";
        }
    };
</script>

<div class="gw-rv-attributes" bind:this={attributeFiltersRef}>
    <FormLayout>
        {#each filters as filter}
            {@const isFiltered = Object.prototype.hasOwnProperty.call(
                selectedFilters,
                `${filterType}[${filter.id}]`
            )}

            <div class="gw-rv-attributes__list">
                <FormLayoutItem
                    label={""}
                    helpText={""}
                    errors={errors[filter.id] || []}
                    gap="8px"
                    id={`gw-attributes-${filter.id}`}
                    {namespace}
                >
                    <div class="gw-rv-attributes__title-block">
                        <span class="gw-rv-attributes__label">
                            {filter.propertyName}
                        </span>
                        {#if isFiltered}
                            <BaseButton
                                class="gw-rv-attribute__clear"
                                on:click={() => {
                                    const initVal = getNewFilterValueByType(
                                        filter.content.type
                                    );
                                    filterFormManager.updateValue(
                                        `${filterType}[${filter.id}]`,
                                        initVal
                                    );
                                }}
                            >
                                <Icon width={12} height={12}>
                                    <CloseIcon />
                                </Icon>
                                <T
                                    namespace="reviews.ReviewsProductPageWidget"
                                    translationKey="filter_clear"
                                />
                            </BaseButton>
                        {/if}
                    </div>

                    {#if filter.content.type === ATTRIBUTE_TYPE.SINGLE_VALUE}
                        {#if filter.content.asDropdown}
                            <FormManagerController
                                let:field
                                name={`${filterType}[${filter.id}]`}
                            >
                                <div class="gw-rv-attributes__select-single">
                                    <Dropdown
                                        placeholderText={"Select answer"}
                                        items={filter.content.options?.map(
                                            (option) => ({
                                                label: option,
                                                value: option,
                                            })
                                        ) || []}
                                        value={field.value}
                                        on:change={field.onChange}
                                        inputAttributes={{
                                            name: `$select-${filter.id}`,
                                        }}
                                    />
                                </div>
                            </FormManagerController>
                        {:else}
                            <FormManagerController
                                name={`${filterType}[${filter.id}]`}
                                let:field
                            >
                                <FormSingleChooseField
                                    options={filter.content.options.map(
                                        (opt) => ({
                                            label: opt,
                                            value: opt,
                                            disabled: false,
                                        })
                                    ) || []}
                                    value={field.value}
                                    name={String(filter.id)}
                                    on:change={field.onChange}
                                />
                            </FormManagerController>
                        {/if}
                    {/if}

                    {#if filter.content.type === ATTRIBUTE_TYPE.MULTI_VALUE}
                        <FormManagerController
                            name={`${filterType}[${filter.id}]`}
                            let:field
                        >
                            <FormMultipleChooseField
                                options={filter.content.options?.map(
                                    (option) => ({
                                        label: option,
                                        value: option,
                                    })
                                ) || []}
                                on:change={field.onChange}
                                value={field.value || []}
                            />
                        </FormManagerController>
                    {/if}

                    {#if filter.content.type === ATTRIBUTE_TYPE.RANGE}
                        <FormManagerController
                            name={`${filterType}[${filter.id}]`}
                            let:field
                        >
                            <Range
                                items={filter.content.options || []}
                                name={filter.id}
                                value={field.value}
                                minLabel={filter.content.minLabel}
                                maxLabel={filter.content.maxLabel}
                                isRange
                                on:change={field.onChange}
                            />
                        </FormManagerController>
                    {/if}

                    {#if filter.content.type === ATTRIBUTE_TYPE.CENTERED_RANGE}
                        <FormManagerController
                            name={`${filterType}[${filter.id}]`}
                            let:field
                        >
                            <Range
                                items={filter.content.options || []}
                                name={filter.id}
                                value={field.value}
                                minLabel={filter.content.minLabel}
                                midLabel={filter.content.midLabel}
                                maxLabel={filter.content.maxLabel}
                                on:change={field.onChange}
                            />
                        </FormManagerController>
                    {/if}

                    {#if filter.content.type === ATTRIBUTE_TYPE.DATE}
                        {@const component = filter.content.component.split("_")}

                        <FormManagerController
                            name={`${filterType}[${filter.id}]`}
                            let:field
                        >
                            <SelectDate
                                {component}
                                dayItems={[...Array(31)].map((_, i) => ({
                                    label: `${i + 1}`,
                                    value: i + 1,
                                }))}
                                monthItems={DATES}
                                yearItems={[...Array(101)].map((_, i) => ({
                                    label: `${new Date().getFullYear() - i}`,
                                    value: new Date().getFullYear() - i,
                                }))}
                                value={field.value}
                                on:change={field.onChange}
                            />
                        </FormManagerController>
                    {/if}

                    {#if filter.content.type === ATTRIBUTE_TYPE.LOCATION}
                        <FormManagerController
                            name={`${filterType}[${filter.id}]`}
                            let:field
                        >
                            <div class="gw-rv-attributes__select-single">
                                <Dropdown
                                    searchable
                                    placeholderText={"Select country"}
                                    items={COUNTRIES.map((c) => {
                                        return {
                                            label: c.value,
                                            value: c.code,
                                        };
                                    })}
                                    value={field.value}
                                    on:change={field.onChange}
                                    inputAttributes={{
                                        name: `$select-${filter.id}`,
                                    }}
                                />
                            </div>
                        </FormManagerController>
                    {/if}
                </FormLayoutItem>
            </div>
        {/each}
    </FormLayout>
</div>

<style lang="postcss">
    .gw-rv-attributes {
        display: contents;

        &__list {
            padding: 0.75em 0 0;
        }

        &__title-block {
            display: flex;
            align-items: center;
            gap: 1em;
            margin-bottom: 0.5em;
        }

        &__label {
            font-size: 16px;
            font-weight: 500;
            line-height: 20px;
            color: #1d2939;
        }

        &__select-single {
            display: flex;
        }
    }

    :global(.gw-rv-listing-layout__filter-clear) {
        font-size: 1em;
        font-style: normal;
        line-height: 20px;
        color: #6d6e85;
        cursor: pointer;
    }
</style>
